import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getAllTags, createTag, updateTag, deleteTag } from '../../services/tagsApi';
import TagEditModal from './components/TagEditModal';
import Head from '../../layout/head/Head';
import Content from '../../layout/content/Content';
import {
    Block,
    BlockDes,
    BlockHeadContent,
    BlockTitle,
    BlockBetween,
    BlockHead,
    DataTableHead,
    DataTableItem,
    DataTableRow,
    Icon,
    PaginationComponent,
    PreviewAltCard,
    DataTable,
    DataTableBody,
    TooltipComponent
} from '../../components/Component';
import {
    Button,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    UncontrolledDropdown,
    Spinner,
    Modal, // For delete confirmation
    ModalBody,
    ModalFooter,
    ModalHeader
} from 'reactstrap';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import { toast } from 'react-toastify';

const TagsPage = () => {
    const axios = useAxiosPrivate();
    const queryClient = useQueryClient();

    const [searchText, setSearchText] = useState("");
    const [onSearch, setOnSearch] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [currentTag, setCurrentTag] = useState(null); // For editing or creating
    const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
    const [tagToDelete, setTagToDelete] = useState(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [smOption, setSmOption] = useState(false); // For mobile view options

    // Fetch Tags Query
    const { data: tagsData, isLoading: isLoadingTags, error: fetchError } = useQuery(
        ['tags', searchText, currentPage, itemsPerPage], // Include dependencies for refetching
        async () => {
            const allTags = await getAllTags(axios, searchText);
            // Manual pagination if API doesn't support it directly
            const start = (currentPage - 1) * itemsPerPage;
            const end = start + itemsPerPage;
            return {
                docs: allTags.slice(start, end),
                totalDocs: allTags.length
            };
        },
        { keepPreviousData: true }
    );

    const tags = tagsData?.docs || [];
    const totalItems = tagsData?.totalDocs || 0;

    // Create Tag Mutation
    const createTagMutation = useMutation(
        (newTagData) => createTag(axios, newTagData),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('tags');
                toast.success('Tag created successfully!');
                setShowEditModal(false);
                setCurrentTag(null);
            },
            onError: (error) => {
                toast.error(`Error creating tag: ${error.message || 'An unexpected error occurred.'}`);
            },
        }
    );

    // Update Tag Mutation
    const updateTagMutation = useMutation(
        ({ tagId, updateData }) => updateTag(axios, tagId, updateData),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('tags');
                toast.success('Tag updated successfully!');
                setShowEditModal(false);
                setCurrentTag(null);
            },
            onError: (error) => {
                toast.error(`Error updating tag: ${error.message || 'An unexpected error occurred.'}`);
            },
        }
    );

    // Delete Tag Mutation
    const deleteTagMutation = useMutation(
        (tagId) => deleteTag(axios, tagId),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('tags');
                toast.success('Tag deleted successfully!');
                setShowDeleteConfirmModal(false);
                setTagToDelete(null);
            },
            onError: (error) => {
                toast.error(`Error deleting tag: ${error.message || 'An unexpected error occurred.'}`);
                setShowDeleteConfirmModal(false);
                setTagToDelete(null);
            },
        }
    );

    const handleSaveTag = (tagData) => {
        if (tagData.id) { // Existing tag, so update
            updateTagMutation.mutate({ tagId: tagData.id, updateData: { name: tagData.name } });
        } else { // New tag
            createTagMutation.mutate({ name: tagData.name });
        }
    };

    const handleOpenCreateModal = () => {
        setCurrentTag(null); // No current tag means it's for creation
        setShowEditModal(true);
    };

    const handleEditTag = (tag) => {
        setCurrentTag(tag);
        setShowEditModal(true);
    };

    const handleDeleteTag = (tag) => {
        setTagToDelete(tag);
        setShowDeleteConfirmModal(true);
    };

    const confirmDeleteTag = () => {
        if (tagToDelete) {
            deleteTagMutation.mutate(tagToDelete.id); // Assuming id is the identifier
        }
    };

    const toggleSearch = () => setOnSearch(!onSearch);

    const handleSearchInputChange = (e) => {
        setSearchText(e.target.value);
        setCurrentPage(1); // Reset to first page on new search
    };

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    if (isLoadingTags && !tagsData) { // Show loading only on initial load
        return (
            <Content>
                <div className="text-center p-5">
                    <Spinner color="primary" />
                    <p>Loading tags...</p>
                </div>
            </Content>
        );
    }

    if (fetchError) {
        return (
            <Content>
                <PreviewAltCard>
                    <p className="text-danger text-center">Error fetching tags: {fetchError.message}</p>
                </PreviewAltCard>
            </Content>
        );
    }

    return (
        <React.Fragment>
            <Head title="Manage Tags" />
            <Content>
                <BlockHead size="sm">
                    <BlockBetween>
                        <BlockHeadContent>
                            <BlockTitle tag="h3" page>
                                Tags
                            </BlockTitle>
                            <BlockDes className="text-soft">
                                <p>You have total {totalItems} tags.</p>
                            </BlockDes>
                        </BlockHeadContent>
                        <BlockHeadContent>
                            <div className="toggle-wrap nk-block-tools-toggle">
                                <Button
                                    className={`btn-icon btn-trigger toggle-expand me-n1 ${smOption ? "active" : ""}`}
                                    onClick={() => setSmOption(!smOption)}
                                >
                                    <Icon name="menu-alt-r"></Icon>
                                </Button>
                                <div className="toggle-expand-content" style={{ display: smOption ? "block" : "none" }}>
                                    <ul className="nk-block-tools g-3">
                                        <li className="nk-block-tools-opt">
                                            <Button color="primary" onClick={handleOpenCreateModal}>
                                                <Icon name="plus"></Icon>
                                                <span>Add New Tag</span>
                                            </Button>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </BlockHeadContent>
                    </BlockBetween>
                </BlockHead>

                <Block>
                    <DataTable className="card-stretch">
                        <div className="card-inner-group">
                            <div className="card-inner position-relative card-tools-toggle">
                                <div className="card-title-group">
                                    <div className="card-tools">
                                        {/* Future: Bulk actions */}
                                    </div>
                                    <div className="card-tools me-n1">
                                        <ul className="btn-toolbar gx-1">
                                            <li>
                                                <a
                                                    href="#search"
                                                    onClick={(ev) => {
                                                        ev.preventDefault();
                                                        toggleSearch();
                                                    }}
                                                    className="btn btn-icon search-toggle toggle-search"
                                                >
                                                    <Icon name="search"></Icon>
                                                </a>
                                            </li>
                                            <li className="btn-toolbar-sep"></li>
                                            <li>
                                                <UncontrolledDropdown>
                                                    <DropdownToggle color="tranparent" className="btn btn-trigger btn-icon dropdown-toggle">
                                                        <Icon name="setting"></Icon>
                                                    </DropdownToggle>
                                                    <DropdownMenu end className="dropdown-menu-xs">
                                                        <ul className="link-check">
                                                            <li><span>Show</span></li>
                                                            {[10, 20, 50].map(num => (
                                                                <li className={itemsPerPage === num ? "active" : ""} key={num}>
                                                                    <DropdownItem tag="a" href="#dropdownitem" onClick={(ev) => { ev.preventDefault(); setItemsPerPage(num); setCurrentPage(1); }}>{num}</DropdownItem>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </DropdownMenu>
                                                </UncontrolledDropdown>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                                <div className={`card-search search-wrap ${onSearch ? "active" : ""}`}>
                                    <div className="card-body">
                                        <div className="search-content" style={{ display: "flex", alignItems: "center" }}>
                                            <input
                                                type="text"
                                                className="form-control border-transparent form-focus-none"
                                                placeholder="Search by tag name..."
                                                value={searchText}
                                                onChange={handleSearchInputChange}
                                                style={{ flexGrow: 1 }}
                                            />
                                            <Button className="search-submit btn-icon" onClick={toggleSearch}>
                                                <Icon name="search"></Icon>
                                            </Button>
                                            <Button className="search-close btn-icon" onClick={toggleSearch}>
                                                <Icon name="cross"></Icon>
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <DataTableBody>
                                <DataTableHead>
                                    <DataTableRow><span>Tag Name</span></DataTableRow>
                                    <DataTableRow className="nk-tb-col-tools text-end">Action</DataTableRow>
                                </DataTableHead>

                                {isLoadingTags && tags.length === 0 && ( // Show spinner in table body during re-fetches if table is empty
                                    <DataTableItem>
                                        <DataTableRow className="text-center" style={{ gridTemplateColumns: 'auto' }}>
                                            <Spinner color="primary" />
                                        </DataTableRow>
                                    </DataTableItem>
                                )}


                                {!isLoadingTags && tags.length === 0 && (
                                    <DataTableItem>
                                        <DataTableRow className="text-center" style={{ gridTemplateColumns: 'auto' }}>
                                            No tags found.
                                        </DataTableRow>
                                    </DataTableItem>
                                )}

                                {tags.map((tag) => (
                                    <DataTableItem key={tag.id}>
                                        <DataTableRow>
                                            <span className="tb-lead">{tag.name}</span>
                                        </DataTableRow>
                                        <DataTableRow className="nk-tb-col-tools">
                                            <ul className="nk-tb-actions gx-1">
                                                <li className="nk-tb-action-hidden">
                                                    <TooltipComponent
                                                        tag="a"
                                                        containerClassName="btn btn-trigger btn-icon"
                                                        id={`edit-${tag.id}`}
                                                        icon="edit-alt-fill"
                                                        direction="top"
                                                        text="Edit Tag"
                                                        onClick={() => handleEditTag(tag)}
                                                    />
                                                </li>
                                                <li className="nk-tb-action-hidden">
                                                    <TooltipComponent
                                                        tag="a"
                                                        containerClassName="btn btn-trigger btn-icon"
                                                        id={`delete-${tag.id}`}
                                                        icon="trash-fill"
                                                        direction="top"
                                                        text="Delete Tag"
                                                        onClick={() => handleDeleteTag(tag)}
                                                    />
                                                </li>
                                                <li>
                                                    <UncontrolledDropdown>
                                                        <DropdownToggle tag="a" className="dropdown-toggle btn btn-icon btn-trigger">
                                                            <Icon name="more-h"></Icon>
                                                        </DropdownToggle>
                                                        <DropdownMenu end>
                                                            <ul className="link-list-opt no-bdr">
                                                                <li><DropdownItem tag="a" href="#edit" onClick={(ev) => { ev.preventDefault(); handleEditTag(tag); }}><Icon name="edit"></Icon><span>Edit</span></DropdownItem></li>
                                                                <li><DropdownItem tag="a" href="#delete" onClick={(ev) => { ev.preventDefault(); handleDeleteTag(tag); }}><Icon name="trash"></Icon><span>Delete</span></DropdownItem></li>
                                                            </ul>
                                                        </DropdownMenu>
                                                    </UncontrolledDropdown>
                                                </li>
                                            </ul>
                                        </DataTableRow>
                                    </DataTableItem>
                                ))}
                            </DataTableBody>
                            <div className="card-inner">
                                {totalItems > 0 ? (
                                    <PaginationComponent
                                        itemPerPage={itemsPerPage}
                                        totalItems={totalItems}
                                        paginate={paginate}
                                        currentPage={currentPage}
                                    />
                                ) : (
                                    !isLoadingTags && <div className="text-center"><span className="text-silent">No tags found</span></div>
                                )}
                            </div>
                        </div>
                    </DataTable>
                </Block>
            </Content>

            <TagEditModal
                show={showEditModal}
                onHide={() => {
                    setShowEditModal(false);
                    setCurrentTag(null);
                }}
                tag={currentTag}
                onSave={handleSaveTag}
                isLoading={createTagMutation.isLoading || updateTagMutation.isLoading}
            />

            <Modal isOpen={showDeleteConfirmModal} toggle={() => setShowDeleteConfirmModal(false)}>
                <ModalHeader toggle={() => setShowDeleteConfirmModal(false)}>Confirm Deletion</ModalHeader>
                <ModalBody>
                    Are you sure you want to delete the tag "{tagToDelete?.name}"? This action cannot be undone.
                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={() => setShowDeleteConfirmModal(false)} disabled={deleteTagMutation.isLoading}>
                        Cancel
                    </Button>
                    <Button color="danger" onClick={confirmDeleteTag} disabled={deleteTagMutation.isLoading}>
                        {deleteTagMutation.isLoading ? "Deleting..." : "Delete Tag"}
                    </Button>
                </ModalFooter>
            </Modal>

        </React.Fragment>
    );
};

export default TagsPage;